// Shared constants + small label helpers used by both the routes-list
// rendering and the routes-create form pickers (and, in D3d-part-2, by
// the route-detail card's stops timeline + add-stop form). Lives in a
// .ts file (not .tsx) so the create-form + list + detail components can
// all pull from one place without circular imports.
//
// Extracted from LogisticsRoutesPanel.tsx as the file-size-hygiene
// preamble for D3d-part-2 (the panel was at 311 lines -- soft-warning
// band -- before the stops timeline + add-stop form pushed it past the
// hard 400 cap).
import { formatScheduledForDisplay } from "@/lib/manufacturing/scheduledForPicker";
import type { LogiCarrier, LogiRoute } from "@/lib/logistics";
import type { MfgUnit } from "@/lib/manufacturingClient";
import { unitHandoffOptionLabel } from "@/lib/ops/handoffIdentity";

export const inputClass =
  "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100";
export const primaryBtnClass =
  "rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60";
export const ghostBtnClass =
  "rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100 dark:hover:bg-zinc-700";
// Destructive / outline-red variant for the "Cancel route" transition,
// to set it apart visually from the forward-progress transitions even
// though the underlying action shape is the same.
export const dangerBtnClass =
  "rounded-lg border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-red-800 dark:bg-zinc-800 dark:text-red-200 dark:hover:bg-red-950/40";

export const ROUTE_STATE_LABELS: Record<string, string> = {
  planned: "Planned",
  scheduled: "Scheduled",
  in_transit: "In transit",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

// Imperative button labels for the state-transition action buttons in
// LogisticsRoutesDetailCard. Keyed by target state, not source state,
// because the same target reads as the same action regardless of where
// it came from ("Mark scheduled" applies whether the route is currently
// planned or, hypothetically in some future reopen flow, somewhere else).
export const ROUTE_STATE_ACTION_LABELS: Record<string, string> = {
  scheduled: "Mark scheduled",
  in_transit: "Mark in transit",
  delivered: "Mark delivered",
  cancelled: "Cancel route",
  planned: "Reopen as planned",
};

// State-driven chip Tailwind classes. `cancelled` is muted, `in_transit`
// is the "needs attention" warm tone, `delivered` is success-green.
export const ROUTE_STATE_CHIP_CLASSES: Record<string, string> = {
  planned: "bg-gray-500 text-white",
  scheduled: "bg-emerald-600 text-white",
  in_transit: "bg-amber-600 text-white",
  delivered: "bg-emerald-700 text-white",
  cancelled: "bg-gray-400 text-white",
};

export const ORIGIN_STOP_KIND_FILTER_LABELS: Record<string, string> = {
  plant_origin: "Plant",
  dealer_holding: "Lot / dealer holding",
  end_customer: "Customer site",
  service: "Service",
  other: "Other",
};

export function unitLabel(units: MfgUnit[], unitId: string): string {
  const u = units.find((x) => x.id === unitId);
  return u ? unitHandoffOptionLabel(u) : unitId.slice(0, 8);
}

export function carrierLabel(carriers: LogiCarrier[], carrierId: string | null): string {
  if (!carrierId) return "(carrier-tentative)";
  const c = carriers.find((x) => x.id === carrierId);
  return c ? c.name : carrierId.slice(0, 8);
}

export function formatTimestamp(ts: string | null): string | null {
  if (!ts) return null;
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
}

// D3k: render a route's scheduling for display. Prefers the picker-set
// human-readable label ("Morning" / "8:00–10:00 AM" / "Date only") when
// present; falls back to the formatted scheduled_for timestamp for
// legacy rows + the specific_time picker mode. Same source-of-truth as
// the picker module so display + creation stay in lockstep.
export function formatRouteScheduled(
  route: Pick<LogiRoute, "scheduled_for" | "scheduled_window_label">
): string | null {
  return formatScheduledForDisplay({
    scheduled_for: route.scheduled_for,
    scheduled_window_label: route.scheduled_window_label,
  });
}

export type LogisticsRoutesLabelLookup = {
  units: MfgUnit[];
  carriers: LogiCarrier[];
};
